import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { del } from "@/utils/api-handler";
import { toast } from "sonner";
import { DeleteConfirmationDialog } from "@/components/shared/delete-dialog";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PermissionGate } from "@/components/shared/permission-gate";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { MoreHorizontal, Trash2, Eye } from "lucide-react";
import { CommandAgent } from "@/types/command-agent";

interface CommandAgentActionCellProps {
    row: { original: CommandAgent };
}

export function CommandAgentActionCell({ row }: CommandAgentActionCellProps) {
    const queryClient = useQueryClient();
    const router = useRouter();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const deleteMutation = useProtectedMutation({
        mutationFn: () => del(`/api/command-team/agents/${row.original.id}`),
        requiredPermissions: ["DELETE_COMMAND_AGENT"],
        onSuccess: (response) => {
            if (response.data && response.data.deleted) {
                queryClient.invalidateQueries({ queryKey: ["command-agents"] });
                queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
                toast.success("Command Agent Deleted", {
                    description: `${row.original.name} has been deleted successfully`,
                });
                setDeleteDialogOpen(false);
            } else {
                queryClient.invalidateQueries({ queryKey: ["command-agents"] });
                queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
                toast.success("Command Agent Deleted", {
                    description: `${row.original.name} has been deleted successfully`,
                });
                setDeleteDialogOpen(false);
            }
        },
        onError: (error: any) => {
            console.error("Delete operation failed:", error);
            toast.error("Failed to delete command agent", {
                description:
                    error.response?.data?.message ||
                    "An error occurred while deleting the command agent.",
            });
            setDeleteDialogOpen(false);
        },
    });

    const handleDelete = async () => {
        try {
            await deleteMutation.mutateAsync();
        } catch (error) {
            console.error("Delete operation failed:", error);
        }
    };

    const handleViewDetails = () => {
        router.push(`/workforce/command-team-old/agents/${row.original.id}?name=${encodeURIComponent(row.original.name || "")}`);
    };

    return (
        <div className="flex items-center justify-end">
            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleDelete}
                isLoading={deleteMutation.isPending}
                itemName={row.original.name ?? ""}
                itemType="command agent"
            />
            <div className="flex flex-col items-end">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[150px] py-2">
                        <PermissionGate requiredPermissions={["VIEW_COMMAND_AGENT"]}>
                            <DropdownMenuItem
                                className="justify-start cursor-pointer"
                                onClick={handleViewDetails}
                            >
                                <Eye className="mr-2 h-4 w-4" />
                                Details
                            </DropdownMenuItem>
                        </PermissionGate>
                        <PermissionGate requiredPermissions={["DELETE_COMMAND_AGENT"]}>
                            <DropdownMenuItem
                                className="justify-start cursor-pointer text-destructive hover:text-destructive"
                                onClick={() => setDeleteDialogOpen(true)}
                                disabled={deleteMutation.isPending}
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {deleteMutation.isPending ? "Deleting..." : "Delete"}
                            </DropdownMenuItem>
                        </PermissionGate>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}

